import { forwardRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Mail } from "lucide-react";
import FullLogoIcon from "@/components/shared/FullLogoIcon";

const Footer = forwardRef<HTMLElement>((_, ref) => {
  const { pathname } = useLocation();
  
  const handleLinkClick = (targetPath: string) => {
    if (pathname === targetPath) {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }
  };

  const linkClasses = "text-sm text-muted-foreground hover:text-foreground transition-colors";

  return (
    <footer ref={ref} className="py-16 border-t border-border/50">
      <div className="px-6 md:px-10">
        {/* Main footer content */}
        <div className="flex flex-col lg:flex-row items-start justify-between gap-12">
          {/* Brand + Descriptor */}
          <div className="flex flex-col items-start gap-3 max-w-xs">
            <FullLogoIcon className="h-20 w-auto text-foreground" />
            <span className="text-soft text-sm leading-relaxed">
              Clear scope · Professional process<br />
              Built exclusively for real estate professionals.
            </span>
          </div>

          {/* Multi-column navigation grid */}
          <nav className="grid grid-cols-2 md:grid-cols-5 gap-8 md:gap-12">
            {/* Company */}
            <div className="flex flex-col gap-3">
              <p className="font-semibold text-foreground text-sm">Company</p>
              <Link 
                to="/" 
                onClick={() => handleLinkClick('/')}
                className={linkClasses}
              >
                Home
              </Link>
              <Link 
                to="/about" 
                onClick={() => handleLinkClick('/about')}
                className={linkClasses}
              >
                About
              </Link>
              <Link 
                to="/products" 
                onClick={() => handleLinkClick('/products')}
                className={linkClasses}
              >
                Products
              </Link>
            </div>

            {/* Legal */}
            <div className="flex flex-col gap-3">
              <p className="font-semibold text-foreground text-sm">Legal</p>
              <Link 
                to="/terms" 
                onClick={() => handleLinkClick('/terms')}
                className={linkClasses}
              >
                Terms & Conditions
              </Link>
              <Link 
                to="/privacy" 
                onClick={() => handleLinkClick('/privacy')}
                className={linkClasses}
              >
                Privacy Policy
              </Link>
              <Link 
                to="/dpa" 
                onClick={() => handleLinkClick('/dpa')}
                className={linkClasses}
              >
                Data Processing Addendum
              </Link>
            </div>

            {/* Portals */}
            <div className="flex flex-col gap-3">
              <p className="font-semibold text-foreground text-sm">Portals</p>
              <Link 
                to="/client-portal/auth" 
                onClick={() => handleLinkClick('/client-portal/auth')}
                className={linkClasses}
              >
                Client Portal
              </Link>
              <Link 
                to="/admin/login" 
                onClick={() => handleLinkClick('/admin/login')}
                className={linkClasses}
              >
                Admin Portal
              </Link>
            </div>

            {/* Connect */}
            <div className="flex flex-col gap-3">
              <p className="font-semibold text-foreground text-sm">Connect</p>
              <a 
                href="mailto:sales@siteelevation.com"
                className={`${linkClasses} inline-flex items-center gap-2 break-all`}
              >
                <Mail className="h-4 w-4 shrink-0" />
                sales@siteelevation.com
              </a>
            </div>

            {/* Resources */}
            <div className="flex flex-col gap-3">
              <p className="font-semibold text-foreground text-sm">Resources</p>
              <a 
                href="https://namecheap.pxf.io/siteelevation"
                target="_blank"
                rel="nofollow sponsored noopener"
                className={linkClasses}
              >
                Namecheap
              </a>
            </div>
          </nav>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-6 border-t border-border/30">
          <div className="flex flex-col items-center gap-1">
            <p className="text-sm text-soft leading-relaxed">
              © {new Date().getFullYear()} Site Elevation. All rights reserved.
            </p>
            <p className="text-sm text-soft leading-relaxed">
              Site Elevation is a brand of Watson Growth Partners.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
});
Footer.displayName = "Footer";

export default Footer;
